import React, { useEffect, useRef, useState } from 'react'
import { Play, Pause, SkipBack, SkipForward, Volume2, RotateCcw, Lock } from 'lucide-react'
import InteractiveContentRenderer from '../../../components/interactive/InteractiveContentRenderer'
import KaTeXRenderer from '../../../components/common/KaTeXRenderer'
import type { LectureScene } from '../types/LessonDoc'

// Lấy id video YouTube từ link (null nếu không phải YouTube).
export const ytId = (url?: string): string | null => {
  if (!url) return null
  const m = url.match(/(?:youtu\.be\/|v=|embed\/|shorts\/)([A-Za-z0-9_-]{11})/)
  return m ? m[1] : (/^[A-Za-z0-9_-]{11}$/.test(url) ? url : null)
}

// Dựng 1 khối nội dung để render hình ảnh của cảnh (tái dùng renderer sẵn có).
const visualItem = (s: LectureScene): any | null => {
  if (s.visualType === 'sim' && s.simType) return { type: s.simType, title: '' }
  if (s.visualType === 'formula' && s.formula) return { type: 'formula', content: s.formula, display: true }
  if (s.visualType === 'image' && s.imageSrc) return { type: 'image', src: s.imageSrc, content: s.imageSrc }
  return null
}

// Câu hỏi tương tác trong cảnh — KHÔNG ghi thống kê. Đúng → mở khoá đi tiếp; sai → cho xem lại cảnh trước.
const SceneQuiz: React.FC<{ scene: LectureScene; onPass?: () => void; onReview?: () => void; canReview?: boolean }> = ({ scene, onPass, onReview, canReview }) => {
  const q = scene.question
  const [picked, setPicked] = useState<number | null>(null)
  if (!q || !q.options?.length) return <div className="text-gray-400 text-sm py-10">Câu hỏi chưa hoàn chỉnh.</div>
  const right = picked !== null && picked === q.correctIndex
  const wrong = picked !== null && picked !== q.correctIndex
  const choose = (i: number) => { setPicked(i); if (i === q.correctIndex) onPass?.() }
  return (
    <div className="w-full max-w-2xl mx-auto text-left py-2">
      <div className="text-lg font-bold text-slate-800 mb-3"><KaTeXRenderer content={q.prompt || 'Câu hỏi'} /></div>
      <div className="space-y-2">
        {q.options.map((opt, i) => {
          const show = picked !== null
          const isRight = i === q.correctIndex
          const tone = !show ? 'border-gray-200 hover:border-violet-300'
            : isRight ? 'border-emerald-400 bg-emerald-50'
            : i === picked ? 'border-rose-300 bg-rose-50' : 'border-gray-200 opacity-70'
          return (
            <button key={i} onClick={() => choose(i)} disabled={right}
              className={`w-full text-left px-3 py-2 rounded-lg border text-sm font-medium transition ${tone}`}>
              <span className="font-bold mr-2">{String.fromCharCode(65 + i)}.</span>
              <KaTeXRenderer content={opt} />
              {show && isRight && <span className="ml-2 text-emerald-600 font-bold">✓</span>}
            </button>
          )
        })}
      </div>
      {right && <p className="mt-2 text-sm font-bold text-emerald-600">Chính xác! Bấm ▶▶ để đi tiếp.</p>}
      {wrong && (
        <div className="mt-2 flex items-center gap-2">
          <span className="text-sm font-bold text-rose-500">Chưa đúng.</span>
          <button onClick={() => setPicked(null)} className="inline-flex items-center gap-1 px-2.5 py-1 rounded bg-gray-100 text-gray-700 text-xs font-bold hover:bg-gray-200">Chọn lại</button>
          {canReview && <button onClick={() => onReview?.()} className="inline-flex items-center gap-1 px-2.5 py-1 rounded bg-amber-500 text-white text-xs font-bold hover:bg-amber-600"><RotateCcw size={12} /> Xem lại</button>}
        </div>
      )}
    </div>
  )
}

// Cảnh video đã có (YouTube hoặc .mp4), có cắt đoạn theo giây.
const SceneVideo: React.FC<{ url?: string; clipStart?: number; clipEnd?: number }> = ({ url, clipStart, clipEnd }) => {
  const vref = useRef<HTMLVideoElement | null>(null)
  const id = ytId(url)
  if (id) {
    const p = new URLSearchParams()
    if (clipStart) p.set('start', String(Math.floor(clipStart)))
    if (clipEnd) p.set('end', String(Math.floor(clipEnd)))
    const qs = p.toString()
    return <iframe className="w-full h-full rounded-lg" src={`https://www.youtube.com/embed/${id}${qs ? '?' + qs : ''}`} title="video"
      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
  }
  if (url) return (
    <video ref={vref} className="w-full h-full rounded-lg bg-black" src={url} controls
      onLoadedMetadata={() => { if (clipStart && vref.current) vref.current.currentTime = clipStart }}
      onTimeUpdate={() => { if (clipEnd && vref.current && vref.current.currentTime >= clipEnd) vref.current.pause() }} />
  )
  return <div className="text-gray-400 text-sm py-10">Chưa có link video.</div>
}

// Trình phát video bài giảng tương tác: lần lượt từng cảnh (hình + lời đọc + phụ đề), tự chuyển cảnh.
const ScenePlayer: React.FC<{ scenes: LectureScene[] }> = ({ scenes }) => {
  const [idx, setIdx] = useState(0)
  const [playing, setPlaying] = useState(false)
  const [passed, setPassed] = useState<Set<string>>(new Set())   // id các cảnh câu hỏi đã trả lời đúng
  const audioRef = useRef<HTMLAudioElement | null>(null)
  const timerRef = useRef<number | null>(null)
  const scene = scenes[idx]
  const isQuestion = scene?.visualType === 'question'
  const isVideo = scene?.visualType === 'video'
  const isInteractive = isQuestion || isVideo
  // Cảnh câu hỏi chặn đi tiếp đến khi trả lời đúng (gate).
  const gateLocked = isQuestion && !passed.has(scene.id)

  const clearTimer = () => { if (timerRef.current) { window.clearTimeout(timerRef.current); timerRef.current = null } }
  const advance = () => setIdx((i) => (i + 1 < scenes.length ? i + 1 : (setPlaying(false), i)))
  const goNext = () => { if (!gateLocked) setIdx((i) => Math.min(scenes.length - 1, i + 1)) }

  useEffect(() => {
    clearTimer()
    const a = audioRef.current
    if (!playing) { a?.pause(); return }
    if (scene?.audioUrl && a) {
      a.currentTime = 0
      a.play().catch(() => { /* trình duyệt chặn autoplay → bỏ qua */ })
    } else if (!isInteractive) {
      timerRef.current = window.setTimeout(advance, Math.max(2, scene?.durationSec || 6) * 1000)
    }
    return clearTimer
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idx, playing])

  if (!scenes.length) {
    return <div className="p-6 text-center text-gray-400 text-sm rounded-xl border border-dashed border-gray-200">Chưa có cảnh nào — tạo kịch bản AI hoặc thêm cảnh trong trình sửa.</div>
  }

  const item = scene ? visualItem(scene) : null
  return (
    <div className="rounded-xl overflow-hidden border border-gray-200 bg-white">
      {/* Khung hình 16:9 */}
      <div className="relative bg-slate-900 flex items-center justify-center p-3" style={{ aspectRatio: '16 / 9' }}>
        {isVideo ? (
          <SceneVideo url={scene.videoUrl} clipStart={scene.clipStart} clipEnd={scene.clipEnd} />
        ) : (
          <div className="w-full max-h-full overflow-auto bg-white rounded-lg p-3">
            {isQuestion ? (
              <SceneQuiz key={scene.id} scene={scene} canReview={idx > 0}
                onPass={() => setPassed((p) => new Set(p).add(scene.id))}
                onReview={() => setIdx((i) => Math.max(0, i - 1))} />
            ) : item ? (
              <InteractiveContentRenderer content={[item]} disableAutoScroll />
            ) : (
              <div className="text-center text-xl md:text-2xl font-bold text-slate-800 py-10 px-4">
                <KaTeXRenderer content={scene.caption || scene.voiceover} />
              </div>
            )}
          </div>
        )}
        {scene.caption && item && !isQuestion && (
          <div className="absolute bottom-2 left-2 right-2 text-center pointer-events-none">
            <span className="inline-block bg-black/70 text-white px-3 py-1.5 rounded-lg text-sm max-w-[92%]">
              <KaTeXRenderer content={scene.caption} />
            </span>
          </div>
        )}
      </div>

      {/* Điều khiển */}
      <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 border-t border-gray-100">
        <button onClick={() => setIdx((i) => Math.max(0, i - 1))} disabled={idx === 0}
          className="p-1.5 rounded-lg text-gray-500 hover:bg-gray-200 disabled:opacity-40"><SkipBack size={16} /></button>
        <button onClick={() => setPlaying((p) => !p)}
          className="p-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700">{playing ? <Pause size={16} /> : <Play size={16} />}</button>
        <button onClick={goNext} disabled={idx >= scenes.length - 1 || gateLocked} title={gateLocked ? 'Trả lời đúng câu hỏi để đi tiếp' : ''}
          className="p-1.5 rounded-lg text-gray-500 hover:bg-gray-200 disabled:opacity-40">{gateLocked ? <Lock size={16} /> : <SkipForward size={16} />}</button>
        <span className="text-xs font-bold text-gray-500 ml-1">Cảnh {idx + 1}/{scenes.length}</span>
        {scene?.audioUrl && <Volume2 size={14} className="text-emerald-500" />}
        {gateLocked && <span className="text-[10px] text-amber-600 font-bold ml-1">· trả lời đúng để mở khoá</span>}
        {isVideo && <span className="text-[10px] text-amber-600 font-bold ml-1">· xem xong bấm ▶▶</span>}
        <div className="flex-1 flex items-center gap-1 justify-end">
          {scenes.map((_, i) => (
            <button key={i} onClick={() => setIdx(i)} title={`Cảnh ${i + 1}`}
              className={`w-2 h-2 rounded-full ${i === idx ? 'bg-blue-600' : 'bg-gray-300 hover:bg-gray-400'}`} />
          ))}
        </div>
      </div>

      {scene?.audioUrl && <audio ref={audioRef} src={scene.audioUrl} onEnded={() => { if (!isInteractive) advance() }} preload="none" />}
    </div>
  )
}

export default ScenePlayer
